import React, { useCallback, useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

import {postCheckout} from './helpers/checkout';
import {getCookie} from './helpers/cookies';
import {postLog} from './helpers/log';
import {registerSubscriber} from "./helpers/mailchimp";

import TicketSelectHeaderActions from "./components/TicketSelectHeaderActions";
import WAResidentConfirmation from "./components/WAResidentConfirmation";
import TicketLogin from "./components/TicketLogin";
import MemberCTA from "./components/MemberCTA";
import CalendarPicker from "./components/CalendarPicker";
import CarouselSelector from "./components/CarouselSelector";
import DayOption from "./components/DayOption";
import TimeOption from "./components/TimeOption";
import MemberTicketSelector from "./components/MemberTicketSelector";
import GuestTicketSelector from "./components/GuestTicketSelector";
import EventSchedule from "./components/EventSchedule";
import TicketSelectionSummary from "./components/TicketSelectionSummary";
import DonationOptions from "./components/DonationOptions";
import TicketPaymentForm from "./components/TicketPaymentForm";
import RecaptchaComponent from "./components/RecaptchaComponent";
import Addons from "./components/Addons";
import buildDonationDetail from "./helpers/build-donation-detail";
import { useRoundUp } from "./components/RoundUp";

const isProductionDomain = (domain) =>
    domain == 'www.seattleaquarium.org' || domain == 'seattleaquarium.org' || domain == 'seaaquariumweb.wpenginepowered.com';

// Service fee definitions. Each entry represents a class of service fee.
// To add more classes (e.g., tiered by purchase amount), add entries with
// a `condition` function that receives { ticketCount, cartSubtotal } and
// returns true when the fee should apply.
const buildServiceFees = (domain) => [
    {
        inventoryId: isProductionDomain(domain) ? 8600 : 8581,
        inventoryName: 'Service Fee',
        unitPrice: 5.00,
        quantity: 1,
        addonType: 'doNotTrack',
        condition: ({ cartSubtotal, ticketCount, isMember }) => ticketCount > 0 && !isMember && cartSubtotal,
    },
];

const Tickets = (props) => {
    const {config} = props;
    const serviceFees = buildServiceFees(config.domain);
    const [acfData, setAcfData] = useState(null);
    const [authenticationToken, setAuthenticationToken] = useState(null);
    const [isReadyToFetchMoreDays, setIsReadyToFetchMoreDays] = useState(false);
    const [activeScreen, setActiveScreen] = useState('landing');
    const [activeSummaryScreen, setActiveSummaryScreen] = useState('selection');
    const [memberCredentials, setMemberCredentials] = useState({'memberId': '', 'lastName': ''});
    const [isMember, setIsMember] = useState(false);
    const [memberInfo, setMemberInfo] = useState(null); // used to store object of member information from ACME
    const [memberOptions, setMemberOptions] = useState(null); // used to store object of eligible upgrade options from ACME
    const [isExpired, setIsExpired] = useState(false); // set to true if membership is past expiration date
    const [memberRestrictions, setMemberRestrictions] = useState(null); // used to store object of ticket types and quantities available for the users membership level
    const [isWAResident, setIsWAResident] = useState({isResident: false, isConfirmed: null, zipCode: null});
    const [availableDays, setAvailableDays] = useState([]); // array of day objects used to create DayOption components
    const availableDaysRef = useRef([]);
    const [selectedDay, setSelectedDay] = useState(null); // yyyy-mm-dd
    const [todaysTicketData, setTodaysTicketData] = useState([]); // the ticketing data of the selected day
    const [availableTimes, setAvailableTimes] = useState([]); // array of time objects used to create TimeOption components
    const [selectedTime, setSelectedTime] = useState(null); // hh:mm tt
    const [selectedTimeIndex, setSelectedTimeIndex] = useState(null); // the nth entry of the selected time from the SATAPI ticketing data (used to target correct data to display)
    // const [shouldPromptMembership, setShouldPromptMembership] = useState(false);
    const [isLargeGroup, setIsLargeGroup] = useState(false);
    const [isAtGeneralCapacity, setIsAtGeneralCapacity] = useState(false);
    const [isAtMemberCapacity, setIsAtMemberCapacity] = useState(false);
    const [isOverCapacity, setIsOverCapacity] = useState(false);
    const { shouldRoundUp, setShouldRoundUp, roundUpAmount, setRoundUpAmount } = useRoundUp();
    const [hasPromoCode, setHasPromoCode] = useState(false);
    const [promoCode, setPromoCode] = useState(null);
    const [promoCodeStatus, setPromoCodeStatus] = useState(null); // null, valid, or invalid
    const [willSubscribe, setWillSubscribe] = useState(false);
    const [subscriptionEmail, setSubscriptionEmail] = useState('');
    const [subscriptionEmailStatus, setSubscriptionEmailStatus] = useState(null); // null, valid, or invalid
    const [cartTotal, setCartTotal] = useState('0.00');
    const [donationAmount, setDonationAmount] = useState('0.00');
    const [donationDetails, setDonationDetails] = useState({
        donorRecognitionType: 'self', 
        notes: '', 
        beneficiaryType: 'In honor of',
        beneficiaryCountry: 'US',
        valid: true,
        errors: {},
    });
    const [selectedTickets, setSelectedTickets] = useState({
        'Adult': 0,
        'Youth': 0,
        'Child': 0
    });
    const [selectedMemberTickets, setSelectedMemberTickets] = useState({
        'Member Adult': 0,
        'Member Youth': 0,
        'Member Child': 0,
        'Member Guest Adult': 0, // half priced option
        'Member Guest Youth': 0, // half priced option
        'Member Guest': 0 // plus guest option
    });
    const [checkoutObject, setCheckoutObject] = useState({
        'shoppingCart': {
            'items': [],
            'couponCodes': [],
            'saleChannel': 'online'
        },
        'billingFirstName': '',
        'billingLastName': '',
        'billingAddress1': '',
        'billingAddress2': '',
        'billingCity': '',
        'billingState': '',
        'billingCountry': 'US',
        'billingZipCode': '',
        'billingEmail': '',
        'billingPhoneNumber': '',
        'manualEntryCardNumber': '',
        'expDate': '',
        'cvc': ''
    });
    const [passedRecaptchaV2, setPassedRecaptchaV2] = useState(false);
    const [isProcessingOrder, setIsProcessingOrder] = useState(false);
    const [processingError, setProcessingError] = useState('');
    const [orderNumber, setOrderNumber] = useState(null);
    const [orderAmount, setOrderAmount] = useState(null);
    const [selectedAddons, setSelectedAddons] = useState({});
    const [addonData, setAddonData] = useState(null);

    // Compute applicable service fees based on current ticket selection
    const totalTicketCount =
        Object.values(selectedTickets).reduce((a, b) => a + b, 0) +
        Object.values(selectedMemberTickets).reduce((a, b) => a + b, 0);

    const applicableServiceFees = serviceFees.filter(fee =>
        fee.condition({ ticketCount: totalTicketCount, cartSubtotal: parseFloat(cartTotal), isMember })
    );

    const serviceFeeTotal = applicableServiceFees.reduce(
        (sum, fee) => sum + fee.unitPrice * fee.quantity, 0
    );

    // Retrieve ACF content
    useEffect(() => {
        const fetchACF = async () => {
            await fetch(`https://${config.domain}/wp-json/wp/v2/pages/${config.pageId}?acf_format=standard`)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(response);
                    }

                    return response.json();
                })
                .then(data => {
                    setAcfData(data);
                    // console.log(data);
                })
                .catch(error => {
                    console.log(error);
                })
        };

        fetchACF();
    }, []);

    function getCartData() {
        if((!todaysTicketData.length || (selectedTimeIndex === null))) {
            return {};
        }

        const items = [];

        // non-member tickets
        for (let ticketType in selectedTickets) {
            let ticketingTypeId;
            for (let i = 0; i < todaysTicketData[selectedTimeIndex].prices.length; i++) {
                if (todaysTicketData[selectedTimeIndex].prices[i].applies_to === ticketType) {
                    ticketingTypeId = todaysTicketData[selectedTimeIndex].prices[i].applies_to_id;
                    break;
                }
            }
            if (selectedTickets[ticketType] > 0) {
                items.push({
                    'itemType': 'Event',
                    'eventId': todaysTicketData[selectedTimeIndex].general_event_id,
                    'ticketingTypeId': ticketingTypeId,
                    'quantity': selectedTickets[ticketType]
                });
            }
        }

        // member tickets
        for (let ticketType in selectedMemberTickets) {
            let ticketingTypeId;
            for (let i = 0; i < todaysTicketData[selectedTimeIndex].prices.length; i++) {
                if (todaysTicketData[selectedTimeIndex].prices[i].applies_to === ticketType) {
                    ticketingTypeId = todaysTicketData[selectedTimeIndex].prices[i].applies_to_id;
                    break;
                }
            }
            if (selectedMemberTickets[ticketType] > 0) {
                items.push({
                    'itemType': 'Event',
                    'eventId': todaysTicketData[selectedTimeIndex].member_event_id,
                    'ticketingTypeId': ticketingTypeId,
                    'quantity': selectedMemberTickets[ticketType]
                });
            }
        }

        // add a donation item if needed
        if (donationAmount !== '0.00' || shouldRoundUp) {
            let finalDonationAmount = parseFloat(0);

            if (donationAmount !== '0.00') {
                finalDonationAmount += parseFloat(donationAmount);
            }

            if (shouldRoundUp) {
                finalDonationAmount += parseFloat(roundUpAmount);
            }

            items.push({
                'itemType': 'Inventory',
                'addonType': 'donation',
                'inventoryName': 'Donation',
                'inventoryId': 1703,
                'quantity': 1,
                'unitPrice': finalDonationAmount.toFixed(2),
                'amount': finalDonationAmount.toFixed(2),
                'donationDetail': buildDonationDetail(donationDetails),
            });
        }

        // add add-ons
        if(selectedAddons && addonData) {
            console.log('Processing add-ons');

            for(const [addonId, addonQuantity] of Object.entries(selectedAddons)) {
                const addonAttributes = addonData[addonId];

                if(addonQuantity) {
                    items.push({
                        'itemType': 'Inventory',
                        'addonType': addonAttributes.type,
                        'inventoryName': addonAttributes.name,
                        'inventoryId': addonAttributes.addon_id,
                        'quantity': addonQuantity,
                        'unitPrice': addonAttributes.price.toFixed(2),
                        'amount': (addonAttributes.price * addonQuantity).toFixed(2),
                    });

                    console.log('Adding add-on', {
                        'itemType': 'Inventory',
                        'addonType': addonAttributes.type,
                        'inventoryName': addonAttributes.name,
                        'inventoryId': addonAttributes.addon_id,
                        'quantity': addonQuantity,
                        'unitPrice': addonAttributes.price.toFixed(2),
                        'amount': (addonAttributes.price * addonQuantity).toFixed(2),
                    });
                }
            }
        } else {
            console.log('Add-ons not ready.', selectedAddons, addonData)
        }

        // add service fees
        for (const fee of applicableServiceFees) {
            items.push({
                'itemType': 'Inventory',
                'addonType': fee.addonType,
                'inventoryName': fee.inventoryName,
                'inventoryId': fee.inventoryId,
                'quantity': fee.quantity,
                'unitPrice': fee.unitPrice.toFixed(2),
                'amount': (fee.unitPrice * fee.quantity).toFixed(2),
            });
        }

        const shoppingCart = {
            couponCodes: (hasPromoCode) ? [promoCode] : [],
            items,
            membershipCategoryId: isMember ? memberInfo.primaryCardHolder.categoryId : undefined,
            membershipIds: isMember ? [memberInfo.primaryCardHolder.membershipId] : undefined,
            saleChannel: 'online',
        };

        return shoppingCart;
    }

    // To submit the CORS checkout
    function placeOrder(token) {
        setProcessingError('');
        setIsProcessingOrder(true);

        // populate checkoutObject's shoppingCart items
        let tempObj = checkoutObject;
        tempObj['shoppingCart'] = getCartData();
        console.log(tempObj);
        setCheckoutObject(tempObj);

        // send order to ACME
        async function checkout(cart) {
            var data = null;
            try {
                
                const condensedAddress = [
                    cart.billingAddress1,
                    cart.billingAddress2,
                    cart.billingCity,
                    cart.billingState
                ].filter(x => !!x).join(', ');
                if(willSubscribe && subscriptionEmailStatus === 'valid') {
                    // Subscribe asynchronously and fail silently to avoid impeding checkout process.
                    registerSubscriber(
                        subscriptionEmail,
                        cart.billingFirstName,
                        cart.billingLastName,
                        cart.billingPhoneNumber,
                        isWAResident.isResident,
                        cart.billingZipCode,
                        condensedAddress,
                        isMember ? memberInfo.primaryCardHolder.membershipId : null
                    ).then((response) => console.log(`Registered subscriber with email: ${response}`))
                    .catch(error => console.error('Error registering newsletter subscriber.', error));
                }

                const response = await postCheckout(config, token, cart);

                data = await response.json();
                console.log(data);

                if (!response.ok) {
                    postLog(response.statusText, response.url, config);
                    throw new Error(response);
                }

                // Update ticket count asynchronously
                const ticketId = todaysTicketData[selectedTimeIndex].id;
                const memberTicketsSold = Object.values(selectedMemberTickets).reduce((total, quantity) => total + quantity, 0);
                const guestTicketsSold = Object.values(selectedTickets).reduce((total, quantity) => total + quantity, 0);
                const soldUpdates = [{ type: 'member', sold: memberTicketsSold }, { type: 'general', sold: guestTicketsSold }];
                
                soldUpdates.forEach((soldUpdate) => {
                    if(soldUpdate.sold > 0) {
                        fetch(`https://${config.apiUrl}/api/tickets/${ticketId}/sold`, {
                            method: 'POST',
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": authenticationToken
                            },
                            body: JSON.stringify(soldUpdate)
                        }).then((response) => console.log('Updated tickets sold in database.', response))
                        .catch((error) => console.error('Error updating tickets sold:', error));
                    }
                });

                setIsProcessingOrder(false);
                setActiveSummaryScreen("done");
                setOrderNumber(data.orderNumber);
                setOrderAmount(data.totalAmount);
                window.scrollTo(0, 200);

                // GA4 purchase event
                let mappedItems = data.orderItems.map((item) => {
                    return {
                        item_id: item.itemId,
                        item_name: typeof(item.displayName) === 'undefined' ? item.itemName : item.displayName,
                        quantity: item.quantity,
                        price: item.amount
                    };
                });
                if(typeof(window["gtag"]) !== "undefined") {
                    gtag('event', 'purchase', {
                        currency: 'USD',
                        transaction_id: data.orderNumber,
                        value: data.totalAmount,
                        coupon: hasPromoCode ? promoCode : '',
                        items: mappedItems
                    });
                }
            } catch (error) {
                setIsProcessingOrder(false);
                setProcessingError(data?.message || error.message || 'Processing error');
                console.error(error);
            }
        };

        checkout(tempObj);
    }

    // MNTN Conversion Pixel
    useEffect(() => {
        if (activeSummaryScreen == 'done') {
            console.log('mntn');
            var x=null,p,q,m,
            o="35832",
            l=orderNumber,
            i=orderAmount,
            c="",
            k="",
            g="",
            j="",
            u="",
            shadditional="";
            try{p=top.document.referer!==""?encodeURIComponent(top.document.referrer.substring(0,512)):""}catch(n){p=document.referrer!==null?document.referrer.toString().substring(0,512):""}try{q=window&&window.top&&document.location&&window.top.location===document.location?document.location:window&&window.top&&window.top.location&&""!==window.top.location?window.top.location:document.location}catch(b){q=document.location}try{m=parent.location.href!==""?encodeURIComponent(parent.location.href.toString().substring(0,512)):""}catch(z){try{m=q!==null?encodeURIComponent(q.toString().substring(0,512)):""}catch(h){m=""}}var A,y=document.createElement("script"),w=null,v=document.getElementsByTagName("script"),t=Number(v.length)-1,r=document.getElementsByTagName("script")[t];if(typeof A==="undefined"){A=Math.floor(Math.random()*100000000000000000)}w="dx.mountain.com/spx?conv=1&shaid="+o+"&tdr="+p+"&plh="+m+"&cb="+A+"&shoid="+l+"&shoamt="+i+"&shocur="+c+"&shopid="+k+"&shoq="+g+"&shoup="+j+"&shpil="+u+shadditional;y.type="text/javascript";y.src=("https:"===document.location.protocol?"https://":"http://")+w;r.parentNode.insertBefore(y,r)
        }
    }, [orderAmount]);

    // Authenticate to access SATAPI and save token for 1 year
    useEffect(() => {
        if (!getCookie('SATAPI_token')) {
            const authenticateApp = async () => {
                try {
                    const response = await fetch(`https://${config.apiUrl}/oauth/token`, {
                        method: 'POST',
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            "grant_type": "client_credentials",
                            "client_id": isProductionDomain(config.domain) ? "3" : "1", // prod / dev
                            "client_secret": isProductionDomain(config.domain) ? "apFsFzcyBHGdUIUYPaAr8vHQCUa2GeUwnvQV0mVH" : "hOBRisrrRv9KWJrT2pD52e0JdgaXdl5tqDafnJSd", // prod / dev
                            // "client_id": "1", // dev
                            // "client_id": "3", // prod
                            // "client_secret": "hOBRisrrRv9KWJrT2pD52e0JdgaXdl5tqDafnJSd", // dev
                            // "client_secret": "apFsFzcyBHGdUIUYPaAr8vHQCUa2GeUwnvQV0mVH", // prod
                            "scope": "*",
                        })
                    });
                    if (!response.ok) {
                        throw new Error("Authentication error");
                    }
                    const token = await response.json();
                    setAuthenticationToken(token.access_token);
                    // setCookie('SATAPI_token', token.access_token, 7); // disable cookie
                } catch (error) {
                    console.error(error);
                }
            };

            authenticateApp();
        } else {
            setAuthenticationToken(getCookie('SATAPI_token'));
        }
    }, []);
    
    // generate the next 60 days
    useEffect(() => {
        if (authenticationToken != null) {

            /* first, create an array of 60 objects for the next 60 days */

            let today = new Date().toLocaleString('en-US', {timeZone: 'America/Los_Angeles'});

            let tempArray = [];
            for (let i = 0; i < 60; i++) {
                let nextDay = new Date(new Date(today).setDate(new Date(today).getDate() + i));

                if (acfData && acfData.acf.ticket_dates_cutoff && nextDay > new Date(acfData.acf.ticket_dates_cutoff)) {
                    break;
                }

                tempArray.push(
                    {
                        full_date: nextDay.getFullYear() + '-' + ('0' + (nextDay.getMonth() + 1)).slice(-2) + '-' + ('0' + nextDay.getDate()).slice(-2),
                        date: ('0' + nextDay.getDate()).slice(-2),
                        month: ('0' + (nextDay.getMonth() + 1)).slice(-2),
                        year: nextDay.getFullYear().toString(),
                        nthDay: nextDay.getDay()
                    }
                );
            }
            setAvailableDays(tempArray);
            availableDaysRef.current = tempArray;
            console.log(tempArray);

            /* second, fetch the first eight days and set the corresponding price and capacity information */

            const fetchFirstEightDays = async () => {
                try {
                    let now = new Date();
                    let options = { timeZone: 'America/Los_Angeles' };
                    let timeInSeattle = now.toLocaleString('en-US', options);
                    let currentDate = timeInSeattle.match(/\d{4}/)[0] + '-' + timeInSeattle.split('/')[0].toString().padStart(2, '0') + '-' + timeInSeattle.match(/\/(\d{1,2})\//)[1].toString().padStart(2, '0'); // yyyy-mm-dd
                    const response = await fetch(`https://${config.apiUrl}/api/tickets/daily?date=${currentDate}&days=7`, {
                        method: 'GET',
                        headers: {
                            "Content-Type": "application/json",
                            "Authorization": authenticationToken
                        }
                    });
                    if (!response.ok) {
                        postLog(config, authenticationToken, response.url, response.statusText);
                        throw new Error(response.url + ' ' + response.statusText);
                    }
                    const data = await response.json();
                    // console.log(data.data);
                    tempArray = tempArray.map((item) => {
                        let matchingObject = data.data.find((obj) => obj.ticket_date === item.full_date);
                        if (matchingObject) {
                            return { ...item, ...matchingObject };
                        }
                        return item;
                    });
                    setAvailableDays(tempArray);
                    availableDaysRef.current = tempArray;
                    console.log(tempArray);
                } catch (error) {
                    console.error(error);
                }

                setIsReadyToFetchMoreDays(true);
            };

            fetchFirstEightDays();
        }
    }, [authenticationToken]);

    // fetch more aggregate data for days as needed
    useEffect(() => {
        if (isReadyToFetchMoreDays) {
            // detect when unloaded days come into view and apply a class
            const dayElements = document.querySelectorAll('.carousel-selector__day');

            const observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    entry.isIntersecting ? entry.target.setAttribute('in-view', true) : entry.target.removeAttribute('in-view');
                });
            });
            
            dayElements.forEach((day) => {
                observer.observe(day);
            });
    
            // throttle scrolling event to report which days are in view
            const dayCarousel = document.querySelector('.ticket-select__date-options .carousel-selector__carousel');
            let isScrolling = false;
            let scrollTimeout;
            dayCarousel.addEventListener('scroll', () => {
                if (!isScrolling) {
                    setScrollTimeout();
                    isScrolling = true;
                } else {
                    clearTimeout(scrollTimeout);
                    setScrollTimeout();
                }
            });

            function setScrollTimeout() {
                scrollTimeout = setTimeout(() => {
                    isScrolling = false;
                    fetchMoreDays();
                }, 100);
            }

            // retreive more daily data
            function fetchMoreDays() {
                // determine which days need data
                let visibleDays = document.querySelectorAll('.carousel-selector__day[in-view="true"]');
                let firstDate = visibleDays[0].getAttribute('data-date');

                // request additional data for those days apply retieved data to corresponding days
                const fetchAdditionalDays = async () => {
                    try {
                        const response = await fetch(`https://${config.apiUrl}/api/tickets/daily?date=${firstDate}&days=${visibleDays.length - 1}`, {
                            method: 'GET',
                            headers: {
                                "Content-Type": "application/json",
                                "Authorization": authenticationToken
                            }
                        });
                        if (!response.ok) {
                            postLog(config, authenticationToken, response.url, response.statusText);
                            throw new Error(response.url + ' ' + response.statusText);
                        }
                        const data = await response.json();
                        // console.log(data.data);
                        let tempArray = availableDaysRef.current.map((item) => {
                            let matchingObject = data.data.find((obj) => obj.ticket_date === item.full_date);
                            if (matchingObject && item.ticket_date == undefined) {
                                return { ...item, ...matchingObject };
                            }
                            return item;
                        });
                        setAvailableDays(tempArray);
                        availableDaysRef.current = tempArray;
                        console.log(tempArray);
                    } catch (error) {
                        console.error(error);
                    }
                };

                if (document.querySelectorAll('.carousel-selector__day[in-view="true"][data-loaded="false"]').length > 0) {
                    fetchAdditionalDays();
                }
            }

            // ready to check if the first day should be removed if all the times have passed
            fetchFirstDayTimes();
        }
    }, [isReadyToFetchMoreDays]);

    // fetch the first day's ticket data from SATAPI to determine if the first day should be removed (all times have passed)
    const fetchFirstDayTimes = async function() {
        if (authenticationToken == null) {
            return;
        }

        let firstDate = availableDays[0].full_date;

        const fetchUrl = (isMember && memberCredentials.memberId != '')
        ? `https://${config.apiUrl}/api/tickets?date=${firstDate}&membership_id=${memberInfo.primaryCardHolder.categoryId}`
        : `https://${config.apiUrl}/api/tickets?date=${firstDate}`;

        try {
            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                }
            });
            if (!response.ok) {
                postLog(config, authenticationToken, response.url, response.statusText);
                throw new Error(response.url + ' ' + response.statusText);
            }
            const data = await response.json();
            console.log(data.data);
            if (data.data.length == 0) {
                console.log('no more times for today');
                let tempArray = availableDays.slice(1);
                setAvailableDays(tempArray);
                availableDaysRef.current = tempArray;
                return;
            } else {
                const nowSeattle = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Los_Angeles" }));
                let timesStillAvailable = false;
                data.data.forEach(entry => {
                    let endTime = parseSeattleLocalTime(entry.end_time);
                    if (nowSeattle < endTime) {
                        timesStillAvailable = true;
                    }
                });
                console.log('times still available for today');
                if (!timesStillAvailable) {
                    let tempArray = availableDays.slice(1);
                    setAvailableDays(tempArray);
                    availableDaysRef.current = tempArray;
                }
            }
        } catch (error) {
            console.error(error);
        }
    }

    function parseSeattleLocalTime(str) {
        const [date, time] = str.split(" ");
        const [y, m, d] = date.split("-").map(Number);
        const [hh, mm, ss] = time.split(":").map(Number);

        // Create a Date *as if* the components are UTC
        const utcCandidate = Date.UTC(y, m - 1, d, hh, mm, ss);

        // Determine what that UTC instant looks like in Seattle
        const fmt = new Intl.DateTimeFormat("en-US", {
            timeZone: "America/Los_Angeles",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false
        });

        const parts = fmt.formatToParts(new Date(utcCandidate))
            .reduce((acc, p) => (p.type !== "literal" ? { ...acc, [p.type]: p.value } : acc), {});

        // Interpret those formatted parts as *UTC* to compute the Seattle offset
        const seattleViewedAsUTC = Date.UTC(
            parts.year,
            parts.month - 1,
            parts.day,
            parts.hour,
            parts.minute,
            parts.second
        );

        const offset = seattleViewedAsUTC - utcCandidate;

        // Corrected UTC timestamp for the actual Seattle-local wall time
        return new Date(Date.UTC(y, m - 1, d, hh, mm, ss) - offset);
    }
        
    // fetch a specific day's ticket data from SATAPI when a day is selected
    const fetchData = async function() {
        if (authenticationToken == null || selectedDay == null) {
            return;
        }

        const fetchUrl = (isMember && memberCredentials.memberId != '')
        ? `https://${config.apiUrl}/api/tickets?date=${selectedDay}&membership_id=${memberInfo.primaryCardHolder.categoryId}`
        : `https://${config.apiUrl}/api/tickets?date=${selectedDay}`;

        try {
            const response = await fetch(fetchUrl, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                }
            });
            if (!response.ok) {
                postLog(config, authenticationToken, response.url, response.statusText);
                throw new Error(response.url + ' ' + response.statusText);
            }
            const data = await response.json();
            setTodaysTicketData(data.data);
            console.log(data.data);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        fetchData();
    }, [selectedDay]);

    // set todaysTicketData when it is retrieved from SATAPI
    useEffect(() => {
        // get the current date and time to compare later
        let now = new Date();
        let options = { timeZone: 'America/Los_Angeles' };
        let timeInSeattle = now.toLocaleString('en-US', options);
        let currentDate = timeInSeattle.match(/\d{4}/)[0] + '-' + timeInSeattle.split('/')[0].toString().padStart(2, '0') + '-' + timeInSeattle.match(/\/(\d{1,2})\//)[1].toString().padStart(2, '0'); // yyyy-mm-dd
        let currentMeridien = timeInSeattle.match(/[A-Z]{2}/)[0];
        let currentHour = timeInSeattle.match(/\s(\d+):/)[1].toString().padStart(2, '0');
        let currentHour24 = (currentMeridien == 'PM' && currentHour != 12) ? parseInt(currentHour) + 12 : currentHour;
        let currentMinute = timeInSeattle.match(/:(\d{2}):/)[1];

        let tempArray = [];
        todaysTicketData.forEach((entry, index) => {

            // checks to skip if date is today and time has passed
            if (selectedDay == currentDate) {
                // already past AM
                if (entry.name.match(/\s(\w{2})\s/)[1] == 'AM' && currentMeridien == 'PM') {
                    // console.log('past AM');
                    return;
                }

                // already past hour
                let entryHour24 = entry.name.match(/\s(\w{2})\s/)[1] == 'PM' ? parseInt(entry.name.match(/\s(\d{2}):/)[1]) + 12 : parseInt(entry.name.match(/\s(\d{2}):/)[1]);
                entryHour24 = (entryHour24 == 24) ? 12 : entryHour24;
                if (entryHour24 < currentHour24) {
                    // console.log('past hour');
                    return;
                }

                // already past minute
                if (entry.name.match(/\s(\d{2}):/)[1] == currentHour && entry.name.match(/:(\d{2})\s/)[1] < currentMinute) {
                    // console.log('past minute');
                    return;
                }
            }

            tempArray.push(
                {
                    // isMembersOnly: entry.event_name.includes('Members'),
                    time: entry.name.match(/(\d+:\d+\s\w{2})/)[1],
                    start_time: entry.start_time,
                    prices: entry.prices,
                    seats: entry.seats,
                    thisTimeIndex: index
                }
            );
        });
        tempArray.sort((a, b) => { return new Date(a.start_time) - new Date(b.start_time) });
        setAvailableTimes(tempArray);
    }, [todaysTicketData]);

    // fetch membership restrictions when a day is selected
    useEffect(() => {
        const fetchMembershipRestrictions = async () => {
            try {
                const response = await fetch(`https://${config.apiUrl}/api/membership-restrictions?membership_category_id=${memberInfo.primaryCardHolder.categoryId}&template_id=${todaysTicketData[selectedTimeIndex].template_id}`, {
                    method: 'GET',
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": authenticationToken
                    }
                });
                if (!response.ok) {
                    postLog(config, authenticationToken, response.url, response.statusText);
                    throw new Error(response.url + ' ' + response.statusText);
                }
                const data = await response.json();
                console.log(data.data);
                setMemberRestrictions(data.data);
            } catch (error) {
                console.error(error);
            }
        }

        if (isMember && selectedTimeIndex != null) {
            fetchMembershipRestrictions();
        }
    }, [selectedTimeIndex]);

    // update cartTotal when selectedTickets or selectedMemberTickets change
    const updateCartTotal = () => {
        let total = 0;
        let ticketCount = 0;
        if (todaysTicketData.length > 0 && selectedTime != null) {
            // Iterate by selected ticket type and look up the price once.
            // This avoids double-counting if the API returns duplicate price
            // entries for the same applies_to (e.g., production has been
            // observed returning Member Guest Adult / Youth twice).
            const prices = todaysTicketData[selectedTimeIndex].prices;
            const findPrice = (type) => {
                const entry = prices.find(p => p.applies_to === type);
                return entry && entry.price != null ? parseFloat(entry.price) : 0;
            };

            for (const ticketType in selectedTickets) {
                if (selectedTickets[ticketType] > 0) {
                    total += selectedTickets[ticketType] * findPrice(ticketType);
                    ticketCount += selectedTickets[ticketType];
                }
            }

            for (const ticketType in selectedMemberTickets) {
                if (selectedMemberTickets[ticketType] > 0) {
                    total += selectedMemberTickets[ticketType] * findPrice(ticketType);
                    ticketCount += selectedMemberTickets[ticketType];
                }
            }
        }

        if(addonData && selectedAddons) {
            for(const [addonId, addonQuantity] of Object.entries(selectedAddons)) {
                if(addonQuantity) {
                    total += addonData[addonId].price * addonQuantity;
                }
            }
        }

        const applicableServiceFees = serviceFees.filter(fee =>
            fee.condition({ ticketCount: ticketCount, cartSubtotal: total, isMember })
        );

        const serviceFeeTotal = applicableServiceFees.reduce(
            (sum, fee) => sum + fee.unitPrice * fee.quantity, 0
        );

        // include service fees when tickets are selected
        total += serviceFeeTotal;

        setCartTotal(total.toFixed(2));
    }

    useEffect(updateCartTotal, [selectedTickets, selectedMemberTickets, todaysTicketData, selectedAddons]);

    // display group message if the number of visitors >= 20
    useEffect(() => {
        let ticketCount = Object.values(selectedMemberTickets).reduce((total, current) => total + current, 0);
        ticketCount += Object.values(selectedTickets).reduce((total, current) => total + current, 0)
        if (ticketCount > 19) {
            setIsLargeGroup(true);
        } else {
            setIsLargeGroup(false);
        }
    }, [selectedMemberTickets, selectedTickets]);

    // display membership prompt is non-member is buying 5 or more tickets
    // useEffect(() => {
    //     if (!isMember) {
    //         let nonMemberTicketCount = Object.values(selectedTickets).reduce((total, current) => total + current, 0);
    //         if (nonMemberTicketCount > 4) {
    //             setShouldPromptMembership(true);
    //         } else {
    //             setShouldPromptMembership(false);
    //         }
    //     }
    // }, [selectedTickets]);

    // display capacity message if the number of visitors exceeds availability
    useEffect(() => {
        if (todaysTicketData[selectedTimeIndex] === undefined) {
            return;
        }

        let generalTicketCount = Object.values(selectedTickets).reduce((total, current) => total + current, 0)
        let memberTicketCount = Object.values(selectedMemberTickets).reduce((total, current) => total + current, 0);

        if (generalTicketCount > 0) {
            setIsAtGeneralCapacity((generalTicketCount >= todaysTicketData[selectedTimeIndex].seats.general.available) ? true : false);
        }

        if (memberTicketCount > 0) {
            setIsAtMemberCapacity((memberTicketCount >= todaysTicketData[selectedTimeIndex].seats.member.available) ? true : false);
        }

        if (
            (generalTicketCount > 0 && generalTicketCount > todaysTicketData[selectedTimeIndex].seats.general.available)
            ||
            (memberTicketCount > 0 && memberTicketCount > todaysTicketData[selectedTimeIndex].seats.member.available)
            )
        {
            setIsOverCapacity(true);
        } else {
            setIsOverCapacity(false);
        }

    }, [selectedMemberTickets, selectedTickets]);

    // clear selectedTime and selectedTimeIndex when selectedDay changes
    useEffect(() => {
        setSelectedTime(null);
        setSelectedTimeIndex(null);
    }, [selectedDay]);

    // clear ticket selection
    const resetSelectedTickets = useCallback(() => {
        setIsAtGeneralCapacity(false);

        setSelectedTickets((oldSelectedTickets) => Object
            .keys(oldSelectedTickets)
            .reduce((newSelectedTickets, ticketType) => { 
                newSelectedTickets[ticketType] = 0;
                return newSelectedTickets;
            }, {})
        )
    }, []);

    const resetSelectedMemberTickets = useCallback(() => {
        setIsAtMemberCapacity(false);

        setSelectedMemberTickets((oldSelectedTickets) => Object
            .keys(oldSelectedTickets)
            .reduce((newSelectedTickets, ticketType) => {
                newSelectedTickets[ticketType] = 0;
                return newSelectedTickets;
            }, {})
        )
    }, []);

    const resetAllSelectedTickets = useCallback(() => {
        resetSelectedTickets();
        resetSelectedMemberTickets();
    }, [resetSelectedTickets, resetSelectedMemberTickets]);

    // clear ticket selections when selectedDay or selectedTime changes
    useEffect(() => {
        resetAllSelectedTickets();
    }, [selectedDay, selectedTime]);

    // clear selections when navigating back to the landing screen
    useEffect(() => {
        if (activeScreen === 'landing') {
            if (selectedDay != null) {
                document.querySelector('.carousel-selector__day--selected').classList.remove('carousel-selector__day--selected');
            }

            if (selectedTime != null) {
                document.querySelector('.carousel-selector__time--selected').classList.remove('carousel-selector__time--selected');
            }

            resetAllSelectedTickets();
            setMemberCredentials({'memberId': '', 'lastName': ''});
            setIsMember(false);
            setMemberInfo(null);
            setIsWAResident({isResident: false, isConfirmed: null, zipCode: null});
            setSelectedDay(null);
            setSelectedTime(null);
            setSelectedTimeIndex(null);

            setCheckoutObject({
                'shoppingCart': {
                    'items': [],
                    'couponCodes': [],
                    'saleChannel': 'online'
                },
                'billingFirstName': '',
                'billingLastName': '',
                'billingAddress1': '',
                'billingAddress2': '',
                'billingCity': '',
                'billingState': '',
                'billingCountry': 'US',
                'billingZipCode': '',
                'billingEmail': '',
                'billingPhoneNumber': '',
                'manualEntryCardNumber': '',
                'expDate': '',
                'cvc': ''
            });
        }
    }, [activeScreen]);

    const maxTickets = acfData?.acf?.max_tickets ? parseInt(acfData?.acf?.max_tickets) : 0;

    const totalSelectedMemberTickets = selectedMemberTickets
        ? Object.values(selectedMemberTickets).reduce((total, quantity) => total + quantity, 0)
        : 0;

    const totalSelectedNonMemberTickets =  selectedTickets
        ? Object.values(selectedTickets).reduce((total, quantity) => total + quantity, 0)
        : 0;

    // clear non-member ticket selections when member selections use all capacity
    useEffect(() => {
        if(maxTickets) {
            if(maxTickets - totalSelectedMemberTickets < totalSelectedNonMemberTickets) {
                resetSelectedTickets();
            }
        }
    }, [maxTickets, totalSelectedMemberTickets, totalSelectedNonMemberTickets]);

    const navigateToScreen = function(screen) {
        if(screen === "selection") {
            setPromoCode("");
            setPromoCodeStatus(null);
            setHasPromoCode(false);
            fetchData();
            setActiveScreen("select");
        } else {
            setActiveScreen("summary");
        }

        setActiveSummaryScreen(screen);

        window.scrollTo(0, 200);
    }

    const enabledNavButtons = {
        selection: ['review', 'payment', 'donate'].includes(activeSummaryScreen),
        donate: ['review', 'payment', 'addons'].includes(activeSummaryScreen),
        addons: ['review', 'payment'].includes(activeSummaryScreen),
        payment: activeSummaryScreen === 'review',
        review: false,
        done: false,
    };

    useEffect(() => {
        if(acfData?.acf?.addons?.length && authenticationToken) {
            const addonIds = acfData.acf.addons.map(addon => addon.addon_id).join(',');

            fetch(`https://${config.apiUrl}/api/event/addons?ids=${addonIds}`, {
                method: 'GET',
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": authenticationToken
                },
            })
            .then(async (response) => {
                if (!response.ok) {
                    throw new Error("Authentication error");
                }

                const body = await response.json();
                const data = body.data;
                const mappedAddonData = {};

                for(const addon of data) {
                    mappedAddonData[addon.id] = addon;
                }

                console.log('Setting add-on data:', mappedAddonData);
                setAddonData(mappedAddonData);

                const newSelectedAddons = selectedAddons ? JSON.parse(JSON.stringify(selectedAddons)) : {};

                for(const addon of data) {
                    if(!newSelectedAddons[addon.id]) {
                        newSelectedAddons[addon.id] = 0;
                    }
                }

                setSelectedAddons(newSelectedAddons);
            })
            .catch((error) => console.error('Error fetching add-on data:', error));
        }
    }, [authenticationToken, acfData?.acf?.addons?.length])

    return (
        <>
            {/* Ticket Landing Screen */}
            <div
                class="ticket-landing"
                style={{display: activeScreen === "landing" ? "grid" : "none"}}
            >
                <div className="ticket__card invert">
                    <h5>Daytime Admission</h5>
                    {/*<div className="fieldgroup fieldgroup--checkbox wa-resident-fieldgroup">
                        <input
                            type="checkbox"
                            id="wa-resident"
                            name="wa-resident"
                            onClick={() =>
                                setIsWAResident((isWAResident) => ({...isWAResident, isResident: !isWAResident.isResident, isConfirmed: null}))
                            }
                            checked={isWAResident.isResident}
                        />
                        <label htmlFor="wa-resident">Washington resident?</label>
                    </div>*/}
                    {/*<p className="wa-resident-note">WA residents receive discounted admission</p>*/}
                    {isWAResident.isResident && <WAResidentConfirmation isWAResident={isWAResident} setIsWAResident={setIsWAResident}/>}
                    {isWAResident.isResident && isWAResident.isConfirmed && <MemberCTA />}
                    <button
                        className="button"
                        onClick={() => {
                            setActiveScreen("select");
                            window.scrollTo(0, 200);}
                        }
                        disabled={isWAResident.isResident && !isWAResident.isConfirmed}
                    >
                        Purchase Tickets
                    </button>
                </div>
                <div className="ticket__card invert">
                    <TicketLogin
                        config={config}
                        authenticationToken={authenticationToken}
                        memberCredentials={memberCredentials}
                        setMemberCredentials={setMemberCredentials}
                        isMember={isMember}
                        setIsMember={setIsMember}
                        setMemberInfo={setMemberInfo}
                        setMemberOptions={setMemberOptions}
                        setMemberRestrictions={setMemberRestrictions}
                        setIsExpired={setIsExpired}
                        checkoutObject={checkoutObject}
                        setCheckoutObject={setCheckoutObject}
                        setActiveScreen={setActiveScreen}
                    />
                </div>
                <h5 className="ticket__ad-heading">Visiting Multiple Attractions?</h5>
                <aside class="ticket__ad">
                    {acfData && <img src={acfData.acf.citypass_image.url} alt={acfData.acf.citypass_image.caption}/>}
                    {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.citypass_cta}}></div>}
                </aside>
            </div>

            {/* Ticket Select Screen */}
            <div
                class="ticket-select"
                style={{display: activeScreen === "select" ? "block" : "none"}}
            >
                <h3>Ticket Select</h3>
                <p>
                    <a onClick={() => {
                        setActiveScreen("landing");
                        window.scrollTo(0, 200);
                    }}>
                        Go back
                    </a>
                </p>
                <header className="ticket-select__header">
                    <CalendarPicker
                        acfData={acfData}
                        selectedDay={selectedDay}
                        setSelectedDay={setSelectedDay}
                    />
                    <TicketSelectHeaderActions
                        setActiveScreen={setActiveScreen}
                        isMember={isMember}
                        memberInfo={memberInfo}
                    />
                </header>
                <section className="ticket-select__date-options">
                    {acfData && <p>{acfData.acf.ticket_select_instructions}</p>}
                    {isWAResident.isResident && isWAResident.isConfirmed && (
                        <p>You are continuing as a WA Resident.</p>
                    )}
                    <h4>Visit Date</h4>
                    <CarouselSelector
                        isWAResident={isWAResident}
                        isMember={isMember}
                        selectedDay={selectedDay}
                    >
                        {availableDays.map(day => {
                            return <DayOption
                                acfData={acfData}
                                nthDay={day.nthDay}
                                date={day.date}
                                month={day.month}
                                year={day.year}
                                fullDate={day.full_date}
                                ticketDate={day.ticket_date}
                                seatsAvailableGeneral={day.total_seats_available_general}
                                seatsAvailableMember={day.total_seats_available_member}
                                seatsCapacityGeneral={day.total_seats_capacity_general}
                                seatsCapacityMember={day.total_seats_capacity_member}
                                priceWAResident={day.most_expensive_adult_wa_state}
                                priceNonResident={day.most_expensive_adult}
                                isMember={isMember}
                                isWAResident={isWAResident}
                                selectedDay={selectedDay}
                                setSelectedDay={setSelectedDay}
                                setAvailableTimes={setAvailableTimes}
                            />
                        })}
                    </CarouselSelector>
                    {acfData && <p>{acfData.acf.date_note}</p>}
                    {/*<div class="ticket-select__legend">
                        <span>High Availability</span>
                        <div class="ticket-select__legend-icons ticket-select__legend-icons--high"></div>
                        <div class="ticket-select__legend-span"></div>
                        <div class="ticket-select__legend-icons ticket-select__legend-icons--low"></div>
                        <span>Low Availability</span>
                    </div>*/}
                </section>
                {selectedDay && <section className="ticket-select__time-options">
                    <h4>Arrival Time</h4>
                    <CarouselSelector
                        isWAResident={isWAResident}
                        isMember={isMember}
                        selectedDay={selectedDay}
                        selectedTime={selectedTime}
                    >
                        {availableTimes.map(time => {
                            if (time.prices.length > 0) {
                                return <TimeOption
                                    time={time.time}
                                    prices={time.prices}
                                    seats={time.seats}
                                    thisTimeIndex={time.thisTimeIndex}
                                    isMember={isMember}
                                    isWAResident={isWAResident}
                                    selectedTime={selectedTime}
                                    setSelectedTime={setSelectedTime}
                                    selectedTimeIndex={selectedTimeIndex}
                                    setSelectedTimeIndex={setSelectedTimeIndex}
                                />
                            }
                        })}
                    </CarouselSelector>
                    {acfData && <p>{acfData.acf.arrival_note}</p>}
                </section>}
                {selectedTime && <section className={`ticket-select__ticket-choices ${isAtGeneralCapacity ? 'ticket-select__ticket-choices--general-capacity-reached' : ''} ${isAtMemberCapacity ? 'ticket-select__ticket-choices--member-capacity-reached' : ''}`}>
                    {isMember &&
                        <MemberTicketSelector
                            isWAResident={isWAResident}
                            isMember={isMember}
                            maxTickets={parseInt(acfData?.acf?.max_tickets || 0)}
                            memberInfo={memberInfo}
                            memberOptions={memberOptions}
                            memberRestrictions={memberRestrictions}
                            todaysTicketData={todaysTicketData}
                            seats={todaysTicketData[selectedTimeIndex].seats}
                            selectedTime={selectedTime}
                            selectedTimeIndex={selectedTimeIndex}
                            selectedTickets={selectedTickets}
                            setSelectedTickets={setSelectedTickets}
                            selectedMemberTickets={selectedMemberTickets}
                            setSelectedMemberTickets={setSelectedMemberTickets}
                        />
                    }
                    {!isMember &&
                        <GuestTicketSelector
                            isWAResident={isWAResident}
                            isMember={isMember}
                            maxTickets={parseInt(acfData?.acf?.max_tickets || 0)}
                            todaysTicketData={todaysTicketData}
                            seats={todaysTicketData[selectedTimeIndex].seats}
                            selectedTime={selectedTime}
                            selectedTimeIndex={selectedTimeIndex}
                            selectedTickets={selectedTickets}
                            setSelectedTickets={setSelectedTickets}
                        />
                    }
                    <div className="ticket-select__total">
                        {/* {shouldPromptMembership && <p className="ticket-select__group-text"><i className="fa fa-question-circle"></i>DID YOU KNOW: Since you are buying 4 or more tickets, you may want to save money by becoming a member - which includes free admission for a year and two half-price guests with every visit. <a href="/visit/membership">JOIN TODAY</a></p>} */}
                        {/* isLargeGroup && <p className="ticket-select__group-text"><i className="fa fa-users"></i>GROUP DISCOUNT: A 10% discount is available to groups who register 20 or more visitors, with one person paying for the entire group. Please <a href="/group-policy">register your group</a> in advance to guarantee your desired entry time.</p> */}
                        {isAtGeneralCapacity && <p className="ticket-select__capacity-text"><i className="fa fa-warning"></i>General capacity reached for this time slot.</p>}
                        {isAtMemberCapacity && <p className="ticket-select__capacity-text"><i className="fa fa-warning"></i>Member capacity {isOverCapacity ? 'exceeded' : 'reached'} for this time slot.</p>}
                        {isExpired && <p className="ticket-select__expired-text"><i className="fa fa-warning"></i>Your membership is expired. <a href="/member-portal?login">Renew now</a>.</p>}
                        {serviceFeeTotal > 0 && <>
                            <span className="ticket-select__total-title" style={{fontStyle: 'italic', fontSize: '20px'}}>
                                Subtotal
                            </span>
                            <span className="ticket-select__total-amount" style={{fontWeight: 'normal', fontStyle: 'italic', fontSize: '20px'}}>
                                ${(parseFloat(cartTotal) - serviceFeeTotal).toFixed(2)}
                            </span>
                            <span className="ticket-select__total-title" style={{fontStyle: 'italic', fontSize: '20px'}}>
                                Service fee
                            </span>
                            <span className="ticket-select__total-amount" style={{fontWeight: 'normal', fontStyle: 'italic', fontSize: '20px'}}>
                                ${serviceFeeTotal.toFixed(2)}
                            </span>
                        </>}
                        <span className="ticket-select__total-title">
                            Your total
                        </span>
                        <span className="ticket-select__total-amount">
                            ${cartTotal}
                        </span>
                    </div>
                    <button
                        className="ticket-select__add-to-cart button"
                        onClick={() => {
                            setActiveScreen("summary");
                            window.scrollTo(0, 200);

                            // GA4 begin_checkout event
                            let mappedItems = [];

                            // non-member tickets
                            for (let ticketType in selectedTickets) {
                                let itemId, itemName, itemPrice;
                                for (let i = 0; i < todaysTicketData[selectedTimeIndex].prices.length; i++) {
                                    if (todaysTicketData[selectedTimeIndex].prices[i].applies_to === ticketType) {
                                        itemId = todaysTicketData[selectedTimeIndex].prices[i].applies_to_id;
                                        itemName = todaysTicketData[selectedTimeIndex].prices[i].applies_to;
                                        itemPrice = todaysTicketData[selectedTimeIndex].prices[i].price;
                                        break;
                                    }
                                }
                                if (selectedTickets[ticketType] > 0) {
                                    mappedItems.push({
                                        item_id: itemId,
                                        item_name: itemName,
                                        price: itemPrice,
                                        quantity: selectedTickets[ticketType]
                                    });
                                }
                            }

                            // member tickets
                            for (let ticketType in selectedMemberTickets) {
                                let itemId, itemName, itemPrice;
                                for (let i = 0; i < todaysTicketData[selectedTimeIndex].prices.length; i++) {
                                    if (todaysTicketData[selectedTimeIndex].prices[i].applies_to === ticketType) {
                                        itemId = todaysTicketData[selectedTimeIndex].prices[i].applies_to_id;
                                        itemName = todaysTicketData[selectedTimeIndex].prices[i].applies_to;
                                        itemPrice = todaysTicketData[selectedTimeIndex].prices[i].price;
                                        break;
                                    }
                                }
                                if (selectedTickets[ticketType] > 0) {
                                    mappedItems.push({
                                        item_id: itemId,
                                        item_name: itemName,
                                        price: itemPrice,
                                        quantity: selectedTickets[ticketType]
                                    });
                                }
                            }
                            
                            if(typeof(window["gtag"]) !== "undefined") {
                                gtag('event', 'begin_checkout', {
                                    currency: 'USD',
                                    value: cartTotal,
                                    items: mappedItems
                                });
                            }
                        }}
                        disabled={
                            (Object.values(selectedTickets).reduce((a, b) => a + b, 0) === 0 && Object.values(selectedMemberTickets).reduce((a, b) => a + b, 0) === 0)
                            ||
                            isOverCapacity
                            ||
                            isExpired
                        }
                    >
                        Proceed to Checkout
                    </button>
                </section>}
                {/*<section className="ticket-select__schedule">
                    {selectedDay && <EventSchedule selectedDay={selectedDay} />}
                </section>*/}
            </div>

            {/* Ticket Summary Screen */}
            <div
                className="ticket-summary"
                style={{display: activeScreen === "summary" ? "block" : "none"}}
            >
                <h3>Ticket Summary</h3>
                {/*activeSummaryScreen !== "done" && <div className="ticket-summary__header">
                    {!isMember && <a className="button button--small" href="/visit/membership">Become a Member</a>}
                </div>*/}
                <nav className="ticket-summary__steps">
                    <div className={`fieldgroup fieldgroup--radio${enabledNavButtons.selection ? ' enabled' : ''}`}>
                        <input
                            type="radio"
                            id="selection"
                            value="selection"
                            name="step"
                            onClick={() => {
                                if(enabledNavButtons.selection) {
                                    navigateToScreen('selection');
                                }
                            }}
                            checked={activeSummaryScreen === 'selection'}
                            disabled={enabledNavButtons.selection ? null : 'disabled'}
                        />
                        <label htmlFor="selection">Selection</label>
                    </div>
                    <div className={`fieldgroup fieldgroup--radio${enabledNavButtons.donate ? ' enabled' : ''}`}>
                        <input
                            type="radio"
                            id="donate"
                            value="donate"
                            name="step"
                            onClick={() => {
                                if(enabledNavButtons.donate) {
                                    navigateToScreen('donate');
                                }
                            }}
                            checked={activeSummaryScreen === 'donate'}
                            disabled={enabledNavButtons.donate ? null : 'disabled'}
                        />
                        <label htmlFor="donate">Donate</label>
                    </div>
                    {acfData?.acf?.addons?.length && <div className={`fieldgroup fieldgroup--radio${enabledNavButtons.addons ? ' enabled' : ''}`}>
                        <input
                            type="radio"
                            id="addons"
                            value="addons"
                            name="step"
                            onChange={() => {
                                if(enabledNavButtons.addons) {
                                    navigateToScreen('addons');
                                }
                            }}
                            checked={activeSummaryScreen === 'addons'}
                            disabled={enabledNavButtons.addons ? null : 'disabled'}
                        />
                        <label htmlFor="addons">Add-ons</label>
                    </div>}
                    <div className={`fieldgroup fieldgroup--radio${enabledNavButtons.payment ? ' enabled' : ''}`}>
                        <input
                            type="radio"
                            id="payment"
                            value="payment"
                            name="step"
                            onClick={() => {
                                if(enabledNavButtons.payment) {
                                    navigateToScreen('payment');
                                }
                            }}
                            checked={activeSummaryScreen === 'payment'}
                            disabled={enabledNavButtons.payment ? null : 'disabled'}
                        />
                        <label htmlFor="payment">Payment</label>
                    </div>
                    <div className="fieldgroup fieldgroup--radio">
                        <input type="radio" id="review" value="review" name="step" onClick={(e) => e.preventDefault()} checked={activeSummaryScreen === 'review'}/>
                        <label htmlFor="review">Review</label>
                    </div>
                    <div className="fieldgroup fieldgroup--radio">
                        <input type="radio" id="done" value="done" name="step" onClick={(e) => e.preventDefault()} checked={activeSummaryScreen === 'done'}/>
                        <label htmlFor="done">Done</label>
                    </div>
                </nav>
                <div
                    className="ticket-summary__selection"
                    style={{display: activeSummaryScreen === "selection" ? "block" : "none"}}
                >
                    <TicketSelectionSummary
                        config={config}
                        addonData={addonData}
                        applicableServiceFees={applicableServiceFees}
                        serviceFeeTotal={serviceFeeTotal}
                        authenticationToken={authenticationToken}
                        fetchData={fetchData}
                        isWAResident={isWAResident}
                        isMember={isMember}
                        memberInfo={memberInfo}
                        todaysTicketData={todaysTicketData}
                        setTodaysTicketData={setTodaysTicketData}
                        selectedAddons={selectedAddons}
                        selectedDay={selectedDay}
                        selectedTime={selectedTime}
                        selectedTimeIndex={selectedTimeIndex}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        selectedMemberTickets={selectedMemberTickets}
                        setSelectedMemberTickets={setSelectedMemberTickets}
                        donationAmount={donationAmount}
                        shouldRoundUp={shouldRoundUp}
                        setShouldRoundUp={setShouldRoundUp}
                        roundUpAmount={roundUpAmount}
                        setRoundUpAmount={setRoundUpAmount}
                        hasPromoCode={hasPromoCode}
                        setHasPromoCode={setHasPromoCode}
                        promoCode={promoCode}
                        setPromoCode={setPromoCode}
                        promoCodeStatus={promoCodeStatus}
                        setPromoCodeStatus={setPromoCodeStatus}
                        getCartData={getCartData}
                        setWillSubscribe={setWillSubscribe}
                        willSubscribe={willSubscribe}
                        setSubscriptionEmail={setSubscriptionEmail}
                        subscriptionEmail={subscriptionEmail}
                        setSubscriptionEmailStatus={setSubscriptionEmailStatus}
                        subscriptionEmailStatus={subscriptionEmailStatus}
                        cartTotal={cartTotal}
                        updateCartTotal={updateCartTotal}
                    />
                    <button
                        className="ticket-summary__next button"
                        onClick={() => {
                            navigateToScreen("donate");
                        }}
                    >
                        Next
                    </button>
                </div>
                <div
                    className="ticket-summary__donate"
                    style={{display: activeSummaryScreen === "donate" ? "block" : "none"}}
                >
                    <DonationOptions
                        acfData={acfData}
                        donationAmount={donationAmount}
                        setDonationAmount={setDonationAmount}
                        donationDetails={donationDetails}
                        setDonationDetails={setDonationDetails}
                    />
                    <button
                        className="ticket-summary__next button"
                        onClick={() => {
                            navigateToScreen(acfData?.acf?.addons?.length ? "addons" : "payment");
                        }}
                        disabled={!donationDetails.valid}
                    >
                        {`Continue to ${acfData?.acf?.addons?.length ? 'Add-ons' : 'Payment'}`}
                    </button>
                </div>
                {acfData?.acf?.addons?.length && <div
                    className="ticket-summary__addons"
                    style={{display: activeSummaryScreen === "addons" ? "block" : "none"}}
                >
                    <Addons
                        addonData={addonData}
                        addons={acfData?.acf?.addons}
                        selectedAddons={selectedAddons}
                        setSelectedAddons={setSelectedAddons}
                    />
                    <button
                        className="ticket-summary__next button"
                        onClick={() => {
                            navigateToScreen('payment');;
                        }}
                    >
                        Continue to Payment
                    </button>
                </div>}
                <div
                    className="ticket-summary__payment"
                    style={{display: activeSummaryScreen === "payment" ? "block" : "none"}}
                >
                    <TicketPaymentForm
                        acfData={acfData}
                        cartTotal={cartTotal}
                        donationAmount={donationAmount}
                        checkoutObject={checkoutObject}
                        setCheckoutObject={setCheckoutObject}
                        setActiveSummaryScreen={setActiveSummaryScreen}
                        setSubscriptionEmail={setSubscriptionEmail}
                        setSubscriptionEmailStatus={setSubscriptionEmailStatus}
                    />
                </div>
                <div
                    className="ticket-summary__review"
                    style={{display: activeSummaryScreen === "review" ? "block" : "none"}}
                >
                    <TicketSelectionSummary
                        config={config}
                        addonData={addonData}
                        applicableServiceFees={applicableServiceFees}
                        serviceFeeTotal={serviceFeeTotal}
                        authenticationToken={authenticationToken}
                        activeSummaryScreen={activeSummaryScreen}
                        fetchData={fetchData}
                        isWAResident={isWAResident}
                        isMember={isMember}
                        memberInfo={memberInfo}
                        todaysTicketData={todaysTicketData}
                        setTodaysTicketData={setTodaysTicketData}
                        selectedAddons={selectedAddons}
                        selectedDay={selectedDay}
                        selectedTime={selectedTime}
                        selectedTimeIndex={selectedTimeIndex}
                        selectedTickets={selectedTickets}
                        setSelectedTickets={setSelectedTickets}
                        selectedMemberTickets={selectedMemberTickets}
                        setSelectedMemberTickets={setSelectedMemberTickets}
                        donationAmount={donationAmount}
                        shouldRoundUp={shouldRoundUp}
                        setShouldRoundUp={setShouldRoundUp}
                        roundUpAmount={roundUpAmount}
                        setRoundUpAmount={setRoundUpAmount}
                        hasPromoCode={hasPromoCode}
                        setHasPromoCode={setHasPromoCode}
                        promoCode={promoCode}
                        setPromoCode={setPromoCode}
                        promoCodeStatus={promoCodeStatus}
                        setPromoCodeStatus={setPromoCodeStatus}
                        setWillSubscribe={setWillSubscribe}
                        willSubscribe={willSubscribe}
                        setSubscriptionEmail={setSubscriptionEmail}
                        subscriptionEmail={subscriptionEmail}
                        setSubscriptionEmailStatus={setSubscriptionEmailStatus}
                        subscriptionEmailStatus={subscriptionEmailStatus}
                        getCartData={getCartData}
                        cartTotal={cartTotal}
                        updateCartTotal={updateCartTotal}
                    />
                    <RecaptchaComponent setPassedRecaptchaV2={setPassedRecaptchaV2} v2SiteKey={config.recaptchaKeyV2}/>
                    {processingError && <p className="ticket-summary__error-text">
                        <i className="fa fa-warning"></i>
                        There was a problem placing your order: {processingError}.
                        &nbsp;<a
                            href="#"
                            onClick={(e) => {
                                e.preventDefault();
                                navigateToScreen('payment');
                            }}
                        >Please go back and try again</a>. 
                        If the problem persists please 
                        &nbsp;<a href="https://seattleaquariun-tickets.shop/contact-us">contact us</a>.
                    </p>}
                    <button
                        className="ticket-summary__next button"
                        onClick={() => {
                            window.grecaptcha.execute(config.recaptchaKeyV3, { action: 'submit' }).then((token) => {
                                placeOrder(token); // submit the checkoutObject to ACME
                            });
                        }}
                        disabled={!passedRecaptchaV2 || isProcessingOrder}
                    >
                        {!isProcessingOrder ? 'Place Order' : <i className="fa fa-spinner fa-pulse"></i>}
                    </button>
                </div>
                <div 
                    className="ticket-summary__done"
                    style={{display: activeSummaryScreen === "done" ? "block" : "none"}}
                >
                    <h5>{acfData && acfData.acf.confirmation_text}</h5>
                    <p>Your order number is: {orderNumber}<br/>
                    {checkoutObject.billingEmail}</p>
                    {acfData && <div dangerouslySetInnerHTML={{__html: acfData.acf.contact_text}}></div>}
                    <hr/>
                    <h5>Stay Up To Date</h5>
                    <p>Get news and updates delivered to your inbox.</p>
                    <a className="button" href="http://eepurl.com/h7FeBT" target="_blank">Join Our List</a>
                    <hr/>
                    <a className="button button--icon-left" href="/"><i className="fa fa-chevron-left"></i>Back to the Aquarium</a>
                </div>
            </div>
        </>
    );
}

// ReactDOM.render(
//     <Tickets />,
//     document.getElementById('tickets')
// );

var App = function(config) {
    ReactDOM.render(
        <Tickets config={config} />,
        document.getElementById('tickets')
    );
}

window.App = App;